import React from 'react';
import { Box, Stack } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { posShortLabel } from './meta';

/**
 * Width of the row-checkbox column (px). The same value drives the
 * table's grid template, the min-width calc, and the GroupHeader's
 * letter-cell — keeping them in sync.
 */
export const SELECT_COL_WIDTH = 44;

interface CheckboxProps {
  checked: boolean;
  indeterminate?: boolean;
  onClick: (e: React.MouseEvent) => void;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  indeterminate,
  onClick,
}) => {
  const filled = checked || indeterminate;
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      sx={{
        alignItems: 'center',
        bgcolor: filled ? 'primary.main' : 'transparent',
        border: '2px solid',
        borderColor: filled ? 'primary.main' : 'primary.tableBorder',
        borderRadius: '5px',
        cursor: 'pointer',
        display: 'inline-flex',
        flex: 'none',
        height: 20,
        justifyContent: 'center',
        transition: 'all .12s ease',
        width: 20,
      }}
    >
      {checked && !indeterminate && (
        <CheckIcon sx={{ color: 'common.white', fontSize: 15 }} />
      )}
      {indeterminate && (
        <Box
          sx={{
            bgcolor: 'common.white',
            borderRadius: 1,
            height: '2.5px',
            width: '10px',
          }}
        />
      )}
    </Box>
  );
};

interface SwitchProps {
  on: boolean;
  color?: string;
  onClick: () => void;
}

export const Switch: React.FC<SwitchProps> = ({ on, color, onClick }) => (
  <Box
    component={'button'}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    sx={{
      bgcolor: on ? color ?? 'primary.main' : 'primary.tableBorder',
      border: 'none',
      borderRadius: 999,
      cursor: 'pointer',
      flex: 'none',
      height: 24,
      p: '2px',
      transition: 'background-color .16s ease',
      width: 40,
    }}
  >
    <Box
      sx={{
        bgcolor: 'common.white',
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
        height: 20,
        transform: on ? 'translateX(16px)' : 'none',
        transition: 'transform .16s ease',
        width: 20,
      }}
    />
  </Box>
);

interface POSTagProps {
  pos?: string | null;
  size?: 'sm' | 'md';
}

export const POSTag: React.FC<POSTagProps> = ({ pos, size = 'md' }) => {
  if (!pos) return null;
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: '5px',
        color: 'text.secondary',
        fontSize: size === 'sm' ? 10 : 11,
        fontWeight: 700,
        px: 0.75,
        py: '1px',
        textTransform: 'lowercase',
        whiteSpace: 'nowrap',
      }}
    >
      {posShortLabel(pos)}
    </Box>
  );
};

interface DotProps {
  color: string;
  size?: number;
}

export const Dot: React.FC<DotProps> = ({ color, size = 8 }) => (
  <Box
    sx={{
      bgcolor: color,
      borderRadius: '50%',
      flex: 'none',
      height: size,
      width: size,
    }}
  />
);

interface IconChipProps {
  icon: React.ReactNode;
  text: string;
  color?: string;
}

export const IconChip: React.FC<IconChipProps> = ({ icon, text, color }) => (
  <Stack
    direction={'row'}
    alignItems={'center'}
    spacing={0.875}
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 999,
      color: color ?? 'text.secondary',
      fontSize: 13,
      fontWeight: 600,
      px: 1.25,
      py: 0.5,
      whiteSpace: 'nowrap',
    }}
  >
    <Box component={'span'} sx={{ display: 'inline-flex' }}>
      {icon}
    </Box>
    {text}
  </Stack>
);
